import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import App from "./App.tsx";
import "./index.css";
import { installClickTracking } from "./lib/trackClicks";

/* ─────────── GTM dataLayer helpers ─────────── */
const PRIVATE_ROUTE_RE = /^\/(admin|espace|espace-medecin|espace-candidat|auth|forgot-password|reset-password|page-indisponible)(\/|$)/i;
const isPrivateRoute = () => PRIVATE_ROUTE_RE.test(window.location.pathname || "");
const isInternalTraffic = () => {
  try {
    if ((window as any).__INTERNAL_TRAFFIC__) return true;
    return /(?:^|;\s*)internal_traffic=true/.test(document.cookie || "");
  } catch {
    return false;
  }
};
const dl = () => ((window as any).dataLayer = (window as any).dataLayer || []);

// Wrap dataLayer.push pour ignorer les events sur routes privées / trafic interne,
// même après navigation SPA (où le guard d'index.html n'est pas re-exécuté).
(() => {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  const originalPush = w.dataLayer.push.bind(w.dataLayer);
  w.dataLayer.push = (...args: unknown[]) => {
    if (isPrivateRoute() || isInternalTraffic()) return 0;
    return originalPush(...args);
  };

  const notifyRouteChange = () => {
    w.__IS_PRIVATE_ROUTE__ = isPrivateRoute();
    if (isPrivateRoute() || isInternalTraffic()) {
      if (typeof w.gtag === "function") {
        w.gtag = function () { /* noop on private routes */ };
      }
    }
  };
  notifyRouteChange();
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...a: any[]) {
    const r = origPush.apply(this, a as any);
    notifyRouteChange();
    return r;
  };
  history.replaceState = function (...a: any[]) {
    const r = origReplace.apply(this, a as any);
    notifyRouteChange();
    return r;
  };
  window.addEventListener("popstate", notifyRouteChange);
})();

installClickTracking();

// Localisation sémantique du clic (header / footer / section / page)
const resolveClickLocation = (el: Element | null): string => {
  if (!el) return "page";
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("section")) return "section";
  return "page";
};

// Délégation des clics : tel, mailto, FAQ, social
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (!target?.closest) return;
  const pagePath = window.location.pathname;

  const telLink = target.closest("a[href^='tel:']") as HTMLAnchorElement | null;
  if (telLink) {
    dl().push({
      event: "phone_call_click",
      phone_number: telLink.href.replace("tel:", ""),
      click_location: resolveClickLocation(telLink),
      page_path: pagePath,
    });
  }

  const mailLink = target.closest("a[href^='mailto:']") as HTMLAnchorElement | null;
  if (mailLink) {
    dl().push({
      event: "email_click",
      email_address: mailLink.href.replace("mailto:", ""),
      click_location: resolveClickLocation(mailLink),
      page_path: pagePath,
    });
  }

  // Accordéon FAQ (Radix : button[data-state])
  const faqTrigger = target.closest("button[data-state]") as HTMLButtonElement | null;
  if (faqTrigger && faqTrigger.getAttribute("data-state") === "closed") {
    const clone = faqTrigger.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("svg").forEach((s) => s.remove());
    const questionText = clone.textContent?.trim().replace(/\s+/g, " ") || "";
    if (questionText) {
      dl().push({
        event: "faq_click",
        faq_question: questionText,
        click_location: resolveClickLocation(faqTrigger),
        page_path: pagePath,
      });
    }
  }

  // Réseaux sociaux
  const socialLink = target.closest(
    "a[href*='instagram.com'], a[href*='facebook.com'], a[href*='tiktok.com'], a[href*='whatsapp.com'], a[href*='wa.me'], a[href*='linkedin.com'], a[href*='twitter.com'], a[href*='x.com'], a[href*='youtube.com']"
  ) as HTMLAnchorElement | null;
  if (socialLink) {
    const href = socialLink.href;
    let platform = "other";
    if (href.includes("instagram.com")) platform = "instagram";
    else if (href.includes("facebook.com")) platform = "facebook";
    else if (href.includes("tiktok.com")) platform = "tiktok";
    else if (href.includes("whatsapp.com") || href.includes("wa.me")) platform = "whatsapp";
    else if (href.includes("linkedin.com")) platform = "linkedin";
    else if (href.includes("twitter.com") || href.includes("x.com")) platform = "x";
    else if (href.includes("youtube.com")) platform = "youtube";

    dl().push({
      event: "social_click",
      social_platform: platform,
      click_text: socialLink.textContent?.trim() || "",
      click_location: resolveClickLocation(socialLink),
      page_path: pagePath,
    });
  }

  // CTA tracking — tout élément (ou ancêtre) portant data-cta="..."
  const ctaEl = target.closest("[data-cta]") as HTMLElement | null;
  if (ctaEl) {
    const ctaName = ctaEl.getAttribute("data-cta") || "";
    if (ctaName) {
      const explicitDest = ctaEl.getAttribute("data-cta-destination");
      const hrefDest =
        ctaEl instanceof HTMLAnchorElement
          ? ctaEl.getAttribute("href") || ""
          : (ctaEl.querySelector("a")?.getAttribute("href") || "");
      const destination = explicitDest || hrefDest || "action";

      // Texte affiché (sans icônes SVG)
      const clone = ctaEl.cloneNode(true) as HTMLElement;
      clone.querySelectorAll("svg").forEach((s) => s.remove());
      const ctaText = clone.textContent?.trim().replace(/\s+/g, " ").slice(0, 120) || "";

      dl().push({
        event: "cta_click",
        cta_name: ctaName,
        cta_destination: destination,
        cta_text: ctaText,
        click_location: resolveClickLocation(ctaEl),
        page_path: pagePath,
      });
    }
  }
});

// Scroll depth (25 / 50 / 75 / 90 %)
// Throttled via rAF + cached docHeight to avoid forced reflows on every scroll event.
const firedThresholds = new Set<number>();
let scrollTicking = false;
let cachedDocHeight = 0;
let cachedViewportHeight = 0;

const recomputeDocHeight = () => {
  cachedViewportHeight = window.innerHeight;
  cachedDocHeight = document.documentElement.scrollHeight - cachedViewportHeight;
};

// Recompute on resize and after load (avoid reading scrollHeight inside scroll handler).
window.addEventListener("load", recomputeDocHeight, { passive: true });
window.addEventListener("resize", recomputeDocHeight, { passive: true });
recomputeDocHeight();

window.addEventListener(
  "scroll",
  () => {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => {
      scrollTicking = false;
      // window.scrollY does not force layout; use the cached doc height.
      if (cachedDocHeight <= 0) {
        recomputeDocHeight();
        if (cachedDocHeight <= 0) return;
      }
      const percent = Math.round((window.scrollY / cachedDocHeight) * 100);
      for (const threshold of [25, 50, 75, 90]) {
        if (percent >= threshold && !firedThresholds.has(threshold)) {
          firedThresholds.add(threshold);
          dl().push({
            event: "scroll_depth",
            scroll_percentage: threshold,
            click_location: "page",
            page_path: window.location.pathname,
          });
        }
      }
    });
  },
  { passive: true }
);

// Reset des thresholds à chaque changement de route SPA
let lastPath = window.location.pathname;
const resetScrollOnNav = () => {
  if (window.location.pathname !== lastPath) {
    lastPath = window.location.pathname;
    firedThresholds.clear();
  }
};
window.addEventListener("popstate", resetScrollOnNav);
const _push = history.pushState;
history.pushState = function (...a: any[]) {
  const r = _push.apply(this, a as any);
  resetScrollOnNav();
  return r;
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);
